import React from "react";
import styled from "styled-components";
import CustomSwitch from "../../../CustomSwitch";
import Dropdown from "../../../Dropdown";
import TextView from "../../../TextView";
import MarginBox from "../../Shared/MarginBox";
import { colors } from "../../../../utils";
import { PDF_PREVIEW_STYLES_ENABLED } from "../../../../constants/featureFlags";
import {
  PREVIEW_STYLES,
  PREVIEW_STYLE_OPTIONS,
  PREVIEW_PAGE_OPTIONS,
  PREVIEW_NOTE_STYLES,
  PREVIEW_NOTE_ICON,
  isPreviewShown,
  nextToggledStyle,
  isPreviewPageDimmed,
} from "../../Shared/DocumentBlock/documentPreview";

const Container = styled.div``;

const Row = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

// ---- DL-049 preview-style control (flag-gated) ---------------------------
const PreviewSection = styled.div`
  margin-bottom: 24px;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const FieldLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.darkGrey};
  margin-bottom: 8px;
`;

const Segmented = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0;
  background: ${colors.PrimaryPurple100 || "#EFE9FE"};
  border-radius: 10px;
  padding: 3px;
`;

const SegmentButton = styled.button`
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  text-align: center;
  border: none;
  border-radius: 8px;
  padding: 9px 4px;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;
  color: ${(props) => (props.$active ? colors.white : colors.primary)};
  background: ${(props) =>
    props.$active ? colors.PrimaryPurple || "#9B5AF8" : "transparent"};

  &:focus-visible {
    outline: 2px solid ${colors.PrimaryPurple700 || "#8126DB"};
    outline-offset: 1px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HelpText = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: ${colors.textGrey};
  margin-top: 10px;
`;

// ---- "Preview page" dropdown section (deck: dimmed for Icon List) -----------
const PreviewPageSection = styled.div`
  margin-top: 18px;
  opacity: ${(props) => (props.$dimmed ? 0.45 : 1)};
  transition: opacity 140ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PageLabelRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const PageHint = styled.span`
  font-family: "DM Sans", sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: ${colors.textGrey};
`;

const columnsArray = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
];

// Renders the deck note with the canonical bold phrase, derived from the SAME
// copy constant the unit test locks (so rendered text can never drift from the
// tested string). `bold` is the leading/embedded phrase the deck emphasises.
function BoldNote({ text, bold }) {
  const idx = text.indexOf(bold);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <strong>{bold}</strong>
      {text.slice(idx + bold.length)}
    </>
  );
}

function PreviewStyleControl({ previewStyle, onPreviewStyleChange }) {
  const showPreview = isPreviewShown(previewStyle);
  // Default to "full" when the user turns previews on from the collapsed default.
  const activeStyle = showPreview ? previewStyle : PREVIEW_STYLES.FULL;
  const pageDimmed = isPreviewPageDimmed(activeStyle);

  const handleToggle = () => {
    onPreviewStyleChange(nextToggledStyle(previewStyle));
  };

  return (
    <PreviewSection>
      <SwitchRow>
        <TextView variant="subtitle">Show preview</TextView>
        <CustomSwitch checked={showPreview} onChange={handleToggle} />
      </SwitchRow>

      {showPreview && (
        <>
          <FieldLabel>Preview style</FieldLabel>
          <Segmented role="tablist" aria-label="Preview style">
            {PREVIEW_STYLE_OPTIONS.map((opt) => (
              <SegmentButton
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={activeStyle === opt.value}
                $active={activeStyle === opt.value}
                onClick={() => onPreviewStyleChange(opt.value)}
              >
                {opt.label}
              </SegmentButton>
            ))}
          </Segmented>
          <HelpText>
            {activeStyle === PREVIEW_STYLES.ICON ? (
              <BoldNote text={PREVIEW_NOTE_ICON} bold="Icon List" />
            ) : (
              <BoldNote text={PREVIEW_NOTE_STYLES} bold="every document type" />
            )}
          </HelpText>

          <PreviewPageSection $dimmed={pageDimmed}>
            <PageLabelRow>
              <FieldLabel as="span" style={{ marginBottom: 0 }}>
                Preview page
              </FieldLabel>
              <PageHint>which page to show</PageHint>
            </PageLabelRow>
            <Dropdown
              variant="outlined"
              options={PREVIEW_PAGE_OPTIONS}
              disabled={pageDimmed}
            />
          </PreviewPageSection>
        </>
      )}
    </PreviewSection>
  );
}

export default function Layout({
  margin,
  isApplyMarginOnAllSides,
  allSideMargin,
  showThumbnail,
  handleChange,
  handleAdjustment,
  columns,
  previewStyle,
  onPreviewStyleChange,
}) {
  return (
    <Container>
      {PDF_PREVIEW_STYLES_ENABLED && (
        <PreviewStyleControl
          previewStyle={previewStyle}
          onPreviewStyleChange={onPreviewStyleChange}
        />
      )}
      {/* <Row>
        <TextView variant="subtitle">Display Thumbnail</TextView>
        <CustomSwitch
          checked={showThumbnail}
          onChange={() => handleChange("showThumbnail")}
        />
      </Row>
      <Dropdown
        label={"Number of Columns"}
        labelVariant={"subtitle"}
        variant="outlined"
        options={columnsArray}
        value={columns}
        defaultValue={columns}
        onChange={(e) => handleChange("columns", e.target.value)}
        style={{ marginBottom: 20 }}
      /> */}
      <MarginBox
        margin={margin}
        increaseMargin={(side) => handleAdjustment(side, "increase")}
        decreaseMargin={(side) => handleAdjustment(side, "decrease")}
        isApplyMarginOnAllSides={isApplyMarginOnAllSides}
        allSideMargin={allSideMargin}
        handleMarginCheckBox={() => handleChange("marginCheckbox")}
        handleSliderMarginChange={(value) =>
          handleChange("allSideMargin", value)
        }
        handleMarginChange={(side, value) =>
          handleChange("margin", value, side)
        }
        symbol={"px"}
      />
    </Container>
  );
}
